import React, { useState } from 'react';
import { XMarkIcon, CheckCircleIcon, UserIcon, ClipboardDocumentCheckIcon } from '@heroicons/react/24/outline';
import { StageSelectionData } from '../types/Automation';

interface TaskCompletionStageModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectStage: (stageId: number) => Promise<void>;
  selectionData: StageSelectionData;
  isLoading?: boolean;
}

const TaskCompletionStageModal: React.FC<TaskCompletionStageModalProps> = ({
  isOpen,
  onClose,
  onSelectStage,
  selectionData,
  isLoading = false
}) => {
  const [selectedStageId, setSelectedStageId] = useState<number | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!isOpen) return null;

  const handleSelectStage = async () => {
    if (!selectedStageId) {
      setError('Please select a stage');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      await onSelectStage(selectedStageId);
    } catch (err: any) {
      setError(err.message || 'Failed to update lead stage');
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!submitting) {
      setSelectedStageId(null);
      setError(null);
      onClose();
    }
  };

  const isButtonDisabled = !selectedStageId || submitting || isLoading;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4 text-center">
        {/* Backdrop */}
        <div 
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          onClick={handleClose}
        />

        {/* Modal */}
        <div className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
          {/* Header */}
          <div className="bg-gray-800 px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-green-500">
                  <CheckCircleIcon className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white">
                    Task Completed
                  </h3>
                  <p className="text-sm text-gray-300">
                    Select the outcome for this lead
                  </p>
                </div>
              </div>
              <button
                onClick={handleClose}
                disabled={submitting}
                className="text-gray-300 hover:text-white disabled:opacity-50"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="px-6 py-5">
            {/* Task and Lead Info */}
            <div className="mb-6 space-y-3">
              <div className="flex items-center space-x-3 text-sm text-gray-600">
                <ClipboardDocumentCheckIcon className="h-5 w-5 text-gray-400" />
                <span>
                  <span className="font-medium text-gray-900">{selectionData.taskName}</span>
                  <span className="text-gray-500"> has been completed</span>
                </span>
              </div>
              <div className="flex items-center space-x-3 text-sm text-gray-600">
                <UserIcon className="h-5 w-5 text-gray-400" />
                <span>
                  <span className="text-gray-500">Lead: </span>
                  <span className="font-medium text-gray-900">{selectionData.leadName}</span>
                </span>
              </div>
            </div>

            {/* Stage Selection */}
            <div className="space-y-3">
              <label className="block text-sm font-medium text-gray-700">
                What is the outcome?
              </label>
              
              <div className="grid grid-cols-2 gap-4">
                {/* Primary Stage Option */}
                <button
                  type="button"
                  onClick={() => setSelectedStageId(selectionData.primaryStage.id)}
                  disabled={submitting}
                  className={`
                    relative flex flex-col items-center justify-center p-6 rounded-lg border-2 transition-all
                    ${selectedStageId === selectionData.primaryStage.id
                      ? 'border-green-500 bg-green-50 ring-2 ring-green-200'
                      : 'border-gray-200 bg-white hover:border-green-300 hover:bg-green-50'
                    }
                    ${submitting ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                  `}
                >
                  <div 
                    className="w-12 h-12 rounded-full flex items-center justify-center mb-3"
                    style={{ 
                      backgroundColor: selectionData.primaryStage.color 
                        ? `${selectionData.primaryStage.color}20` 
                        : '#dcfce7'
                    }}
                  >
                    <CheckCircleIcon 
                      className="h-7 w-7" 
                      style={{ 
                        color: selectionData.primaryStage.color || '#22c55e'
                      }}
                    />
                  </div>
                  <span className="text-base font-semibold text-gray-900">
                    {selectionData.primaryStage.name}
                  </span>
                  {selectedStageId === selectionData.primaryStage.id && (
                    <div className="absolute top-2 right-2">
                      <CheckCircleIcon className="h-5 w-5 text-green-500" />
                    </div>
                  )}
                </button>

                {/* Alternate Stage Option */}
                <button
                  type="button"
                  onClick={() => setSelectedStageId(selectionData.alternateStage.id)}
                  disabled={submitting}
                  className={`
                    relative flex flex-col items-center justify-center p-6 rounded-lg border-2 transition-all
                    ${selectedStageId === selectionData.alternateStage.id
                      ? 'border-red-500 bg-red-50 ring-2 ring-red-200'
                      : 'border-gray-200 bg-white hover:border-red-300 hover:bg-red-50'
                    }
                    ${submitting ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                  `}
                >
                  <div 
                    className="w-12 h-12 rounded-full flex items-center justify-center mb-3"
                    style={{ 
                      backgroundColor: selectionData.alternateStage.color 
                        ? `${selectionData.alternateStage.color}20` 
                        : '#fee2e2'
                    }}
                  >
                    <XMarkIcon 
                      className="h-7 w-7" 
                      style={{ 
                        color: selectionData.alternateStage.color || '#ef4444'
                      }}
                    />
                  </div>
                  <span className="text-base font-semibold text-gray-900">
                    {selectionData.alternateStage.name}
                  </span>
                  {selectedStageId === selectionData.alternateStage.id && (
                    <div className="absolute top-2 right-2">
                      <CheckCircleIcon className="h-5 w-5 text-red-500" />
                    </div>
                  )}
                </button>
              </div>
            </div>

            {/* Error Message */}
            {error && (
              <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="bg-gray-50 px-6 py-4 flex items-center justify-between">
            <button
              type="button"
              onClick={handleClose}
              disabled={submitting}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSelectStage}
              disabled={isButtonDisabled}
              className={`
                px-6 py-2 text-sm font-medium text-white rounded-md
                ${isButtonDisabled
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-gray-800 hover:bg-gray-700'
                }
              `}
            >
              {submitting ? (
                <span className="flex items-center space-x-2">
                  <svg className="animate-spin h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  <span>Updating...</span>
                </span>
              ) : (
                'Confirm Selection'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TaskCompletionStageModal;
